import { Injectable } from '@angular/core';
import { AbstractControl, ValidationErrors } from '@angular/forms';
import {
  ImageUploadId,
  ImageUploadValidationFactory,
} from '@helpers/image-upload-validation-factory';
import { BaseChannelMapping } from '@interfaces/catalog/1.0/catalog-image';
import { BehaviorSubject, firstValueFrom, isObservable } from 'rxjs';

@Injectable({
  providedIn: 'root',
})
export class BaseImageService {
  baseChannelsMapping: BaseChannelMapping = {};
  baseChannelsConfigurationMapping = {};
  baseImageFile: File | undefined;
  isBaseImageConfigurationFinished = false;
  isBaseImageConfigurationStarted = false;

  private validationGeneration: Record<string, number> = {};
  private validationPendingSubject = new BehaviorSubject<boolean>(false);
  validationPending$ = this.validationPendingSubject.asObservable();

  get isAnyChannelPending(): boolean {
    return Object.values(this.baseChannelsMapping).some((channel) => channel.pending);
  }

  private updateValidationState() {
    this.validationPendingSubject.next(this.isAnyChannelPending);
  }

  /**
   * Publish pending flags set while rebuilding channel mappings.
   * In-flight passes are stale after a rebuild, so they cannot clear the new pending state.
   */
  syncValidationPending() {
    Object.keys(this.baseChannelsMapping).forEach((channelId) => {
      this.validationGeneration[channelId] = (this.validationGeneration[channelId] ?? 0) + 1;
    });
    this.updateValidationState();
  }

  /** A failed base-image download never starts channel checks, so pending would stay on. */
  clearChannelPending(): void {
    Object.values(this.baseChannelsMapping || {}).forEach((channel) => {
      channel.pending = false;
    });
    this.updateValidationState();
  }

  validateChannel(baseImageControl: AbstractControl, channelId: string): Promise<void> {
    const channel = this.baseChannelsMapping[channelId];
    if (!channel) {
      return Promise.resolve();
    }

    // Use stored validationId if available, otherwise fall back to channelId
    const validationId = channel.validationId || (channelId as ImageUploadId);
    const imageChannelDefinition = ImageUploadValidationFactory.get(validationId as ImageUploadId);
    const generation = (this.validationGeneration[channelId] ?? 0) + 1;
    this.validationGeneration[channelId] = generation;

    let asyncValidatorPromise: Promise<ValidationErrors | null>;
    try {
      // Call the asynchronous validator and convert to a Promise if it's an Observable
      const asyncValidatorResult = imageChannelDefinition.asyncValidator(baseImageControl);
      asyncValidatorPromise = isObservable(asyncValidatorResult)
        ? firstValueFrom(asyncValidatorResult)
        : Promise.resolve(asyncValidatorResult);
    } catch {
      asyncValidatorPromise = Promise.reject(new Error('channel validation failed'));
    }

    channel.pending = true;
    this.updateValidationState();

    const isCurrent = () => this.validationGeneration[channelId] === generation;

    // Perform asynchronous and synchronous validation.
    // A newer pass for this channel owns the flags; stale results must not clear pending
    // or overwrite valid after save starts another check.
    return asyncValidatorPromise
      .then((asyncValidator) => {
        if (!isCurrent()) {
          return;
        }

        const fileSizeAndTypeValidator = imageChannelDefinition.validator(baseImageControl);
        if (asyncValidator || fileSizeAndTypeValidator) {
          channel.valid = false;
          channel.errors = {
            ...(asyncValidator || {}),
            ...(fileSizeAndTypeValidator || {}),
          };
          return;
        }

        channel.valid = true;
        channel.errors = {};
      })
      .catch(() => {
        // Keep a result that already finished. A first pass that rejects has nothing
        // to keep, so mark it invalid and let the instructions render.
        if (!isCurrent() || channel.valid !== undefined) {
          return;
        }
        channel.valid = false;
        channel.errors = { ...({ imageDimensions: true } as ValidationErrors) };
      })
      .finally(() => {
        if (!isCurrent()) {
          return;
        }
        channel.pending = false;
        this.updateValidationState();
      });
  }

  hasSelectedBaseImageChannels() {
    if (!this.baseChannelsMapping) return false;
    else {
      return Object.keys(this.baseChannelsMapping).some(
        (channelId) => this.baseChannelsMapping[channelId].selected,
      );
    }
  }

  isBaseNavigationNextButtonDisabled(currentIndex: number) {
    const channels = Object.keys(this.baseChannelsConfigurationMapping);
    // verify if there any errors on baseChannelsMapping
    const hasErrors = channels.some((channelId) => !this.baseChannelsMapping[channelId].valid);
    return !channels[currentIndex + 1] && hasErrors;
  }

  resetMappings() {
    this.baseChannelsMapping = {};
    this.baseChannelsConfigurationMapping = {};
    // Move every counter forward. Clearing them would let a pass that started
    // before the reset reuse generation 1 and look current.
    Object.keys(this.validationGeneration).forEach((channelId) => {
      this.validationGeneration[channelId] += 1;
    });
    this.isBaseImageConfigurationFinished = false;
    this.isBaseImageConfigurationStarted = false;
    this.updateValidationState();
  }
}
